import apisauce from "apisauce";
import { once } from "./utils";
import { downloadPaginationData } from "./sagas";

const BASE_URL = "https://staging.sparkbackend.cerebry.co/";

const create = (baseURL = BASE_URL) => {
  const api = apisauce.create({
    baseURL,
    headers: {
      "Content-Type": "application/json",
    },
    timeout: 10000,
  });

  const setAccessToken = (token) => {
    return api.setHeader("Authorization", token);
  };

  const setPrincipalSchoolId = (school_id) => {
    return api.setHeader("school-id", school_id);
  };

  const removeAccessToken = () => {
    delete api.headers["Authorization"];
  };

  const urlParams = new URLSearchParams(window.location.search);
  const isDemoAPI =
    urlParams.has("is_demo") && urlParams.get("is_demo") === "true"
      ? "-demo"
      : "";

  const retryApiSauce = (response) => {
    let methodType = response.config.method.toUpperCase();
    switch (methodType) {
      case "GET":
        return api.get(response.config.url, response.config.params);
      case "POST":
        return api.post(response.config.url, response.config.data);
      case "PUT":
        return api.put(response.config.url, response.config.data);
      case "DELETE":
        return api.delete(response.config.url, response.config.params);
      default:
        return api.any(response.config);
    }
  };

  const check401 = once((refreshToken) => {
    api.addAsyncResponseTransform(async (response) => {
      let status = response.status || response.data.status;
      let apiUrl = response.url || response.config.url;
      const apiImmuned401 = `api/v2/users/refresh`;
      if (status == 401 && apiUrl.indexOf(apiImmuned401) < 0) {
        setAccessToken(refreshToken);
        const updatedJwtTokenResponse = await refreshUser();
        if (
          updatedJwtTokenResponse.status != 401 &&
          updatedJwtTokenResponse.data &&
          updatedJwtTokenResponse.data.jwt
        ) {
          let newJwtToken = "jwt " + updatedJwtTokenResponse.data.jwt;
          setAccessToken(newJwtToken);
          // saveJWT(JWT_TOKEN, updatedJwtTokenResponse.data.jwt);
          const updatedResponse = await retryApiSauce(response);
          if (
            updatedResponse &&
            updatedResponse.status != 401 &&
            updatedResponse.data
          ) {
            //this line is not mutating the response, so setting the corresponding values alone.
            //response = updatedResponse;
            response.data = updatedResponse.data;
            response.status = updatedResponse.status;
          }
        } else {
          response.tokenRefreshed = true;
        }
      }
    });
  });

  const loginUser = (user) => {
    return api.post(`api/pisa-dashboard/login/`, user);
  };

  const getMe = (role) => {
    return api.get(`api/pisa-dashboard/me/?role=${role}`);
  };

  const forgotPassword = (user) => {
    return api.post(`api/pisa-dashboard/forgot/`, user);
  };

  const verifyResetCode = (params) => {
    return api.post(`api/pisa-dashboard/verify/`, params);
  };

  const resetPassword = (userInfo) => {
    return api.post(`api/pisa-dashboard/reset/`, userInfo);
  };

  const changePassword = (oldPassword, newPassword) => {
    const passwordObj = {
      new_password: newPassword,
      password: oldPassword,
    };
    return api.post(`api/pisa-dashboard/change/`, passwordObj);
  };

  const logoutUser = () => {
    return api.post(`api/pisa-dashboard/logout/`);
  };

  const refreshUser = () => {
    return api.post(`api/v2/users/refresh/`);
  };

  const fetchMinistryDashboardOverviewData = (subject) => {
    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-overview-dashboard-data/`
    );
  };

  const fetchTeacherEngagementCount = (subject, district) => {
    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-teacher-engagement-count/${
        district ? `?district=${district}` : ""
      }`
    );
  };

  const fetchTeacherEngagementData = (subject, district) => {
    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-teacher-engagement-data/${
        district ? `?district=${district}` : ""
      }`
    );
  };

  const fetchStudentEngagementCount = (subject, district) => {
    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-student-engagement-count/${
        district ? `?district=${district}` : ""
      }`
    );
  };

  const fetchStudentEngagementData = (subject, district) => {
    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-student-engagement-data/${
        district ? `?district=${district}` : ""
      }`
    );
  };

  const fetchStudentProficientLvlTwoCount = (subject, district, round) => {
    let queryParamAppendStr = "";

    if (district) {
      queryParamAppendStr += `?district=${district}`;
    }
    if (round) {
      queryParamAppendStr += `${district ? "&" : "?"}round_no=${round}`;
    }

    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-proficiency-level-2-count/${queryParamAppendStr}`
    );
  };

  const fetchStudentProficientLvlTwoData = (subject, district, round) => {
    let queryParamAppendStr = "";

    if (district) {
      queryParamAppendStr += `?district=${district}`;
    }
    if (round) {
      queryParamAppendStr += `${district ? "&" : "?"}round_no=${round}`;
    }

    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-proficiency-level-2-data/${queryParamAppendStr}`
    );
  };

  const fetchMinistryTotalQuestionsAttemptCount = (subject, district, round) => {
    let queryParamAppendStr = "";

    if (district) {
      queryParamAppendStr += `?district=${district}`;
    }
    if (round) {
      queryParamAppendStr += `${district ? "&" : "?"}round_no=${round}`;
    }

    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-question-attempted-count/${queryParamAppendStr}`
    );
  };

  const fetchMinistryTotalQuestionsAttemptData = (subject, district, round) => {
    let queryParamAppendStr = "";

    if (district) {
      queryParamAppendStr += `?district=${district}`;
    }
    if (round) {
      queryParamAppendStr += `${district ? "&" : "?"}round_no=${round}`;
    }

    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-question-attempted-data/${queryParamAppendStr}`
    );
  };

  const fetchMinistryStudentImprovementCount = (subject, district, round) => {
    let queryParamAppendStr = "";

    if (district) {
      queryParamAppendStr += `?district=${district}`;
    }
    if (round) {
      queryParamAppendStr += `${district ? "&" : "?"}round_no=${round}`;
    }

    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-improvement-count/${queryParamAppendStr}`
    );
  };

  const fetchMinistryStudentImprovementData = (subject, district, round) => {
    let queryParamAppendStr = "";

    if (district) {
      queryParamAppendStr += `?district=${district}`;
    }
    if (round) {
      queryParamAppendStr += `${district ? "&" : "?"}round_no=${round}`;
    }

    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-improvement-data/${queryParamAppendStr}`
    );
  };

  const fetchTeacherEngagementPercentageTable = (
    subject,
    district,
    school,
    sort,
    limit,
    offset
  ) => {
    let queryParamAppendStr = "";
    if (district && school && school.length > 2) {
      queryParamAppendStr += `?district=${district}&school=${school}&limit=${limit}&offset=${offset}`;
    } else if (district) {
      queryParamAppendStr += `?district=${district}&limit=${limit}&offset=${offset}`;
    } else if (school && school.length > 2) {
      queryParamAppendStr += `?school=${school}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `?limit=${limit}&offset=${offset}`;
    }
    if (sort && sort.length > 0) {
      queryParamAppendStr += `&sort=${sort}`;
    }

    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-teacher-login-atleast-once-data/${queryParamAppendStr}`
    );
  };

  const fetchTeacherEngagementHoursTable = (
    subject,
    district,
    format,
    school,
    sort,
    limit,
    offset
  ) => {
    let queryParamAppendStr = "";
    if (district && school && school.length > 2) {
      queryParamAppendStr += `?district=${district}&school=${school}&time_format=${format}&limit=${limit}&offset=${offset}`;
    } else if (district) {
      queryParamAppendStr += `?district=${district}&time_format=${format}&limit=${limit}&offset=${offset}`;
    } else if (school && school.length > 2) {
      queryParamAppendStr += `?school=${school}&time_format=${format}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `?time_format=${format}&limit=${limit}&offset=${offset}`;
    }
    if (sort && sort.length > 0) {
      queryParamAppendStr += `&sort=${sort}`;
    }
    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-detailed-teacher-engagement-data/${queryParamAppendStr}`
    );
  };

  const fetchStudentEngagementPercentageTable = (
    subject,
    district,
    school,
    sort,
    limit,
    offset
  ) => {
    let queryParamAppendStr = "";
    if (district && school && school.length > 2) {
      queryParamAppendStr += `?district=${district}&school=${school}&limit=${limit}&offset=${offset}`;
    } else if (district) {
      queryParamAppendStr += `?district=${district}&limit=${limit}&offset=${offset}`;
    } else if (school && school.length > 2) {
      queryParamAppendStr += `?school=${school}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `?limit=${limit}&offset=${offset}`;
    }
    if (sort && sort.length > 0) {
      queryParamAppendStr += `&sort=${sort}`;
    }
    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-student-login-atleast-once-data/${queryParamAppendStr}`
    );
  };

  const fetchStudentEngagementHoursTable = (
    subject,
    district,
    school,
    sort,
    limit,
    offset
  ) => {
    let queryParamAppendStr = "";
    if (district && school && school.length > 2) {
      queryParamAppendStr += `?district=${district}&school=${school}&limit=${limit}&offset=${offset}`;
    } else if (district) {
      queryParamAppendStr += `?district=${district}&limit=${limit}&offset=${offset}`;
    } else if (school && school.length > 2) {
      queryParamAppendStr += `?school=${school}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `?limit=${limit}&offset=${offset}`;
    }
    if (sort && sort.length > 0) {
      queryParamAppendStr += `&sort=${sort}`;
    }
    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-detailed-student-engagement-data/${queryParamAppendStr}`
    );
  };

  const fetchStudentProficientLvlTwoPercentTable = (
    subject,
    district,
    school,
    sort,
    limit,
    offset,
    round
  ) => {
    let queryParamAppendStr = "";
    let params = {};
    if (district) {
      params["district"] = district;
    }
    if (school && school.length > 2) {
      params["school"] = school;
    }
    if (round) {
      params["round_no"] = round;
    }
    params["limit"] = limit;
    params["offset"] = offset;
    if (sort && sort.length > 0) {
      params["sort"] = sort;
    }

    for (let key in params) {
      queryParamAppendStr += `${key}=${params[key]}&`;
    }
    //remove the last '&' character
    queryParamAppendStr = queryParamAppendStr.slice(0, -1);


    // if (district && school && school.length > 2) {
    //   queryParamAppendStr += `?district=${district}&school=${school}&limit=${limit}&offset=${offset}`;
    // } else if (district) {
    //   queryParamAppendStr += `?district=${district}&limit=${limit}&offset=${offset}`;
    // } else if (school && school.length > 2) {
    //   queryParamAppendStr += `?school=${school}&limit=${limit}&offset=${offset}`;
    // } else {
    //   queryParamAppendStr += `?limit=${limit}&offset=${offset}`;
    // }
    // if (sort && sort.length > 0) {
    //   queryParamAppendStr += `&sort=${sort}`;
    // }

    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-percentage-student-proficiency-level2-data/?${queryParamAppendStr}`
    );
  };

  const fetchStudentProficientLvlTwoNumberTable = (
    subject,
    district,
    school,
    sort,
    limit,
    offset,
    round
  ) => {
    let queryParamAppendStr = "";
    let params = {};
    if (district) {
      params["district"] = district;
    }
    if (school && school.length > 2) {
      params["school"] = school;
    }
    if (round) {
      params["round_no"] = round;
    }
    params["limit"] = limit;
    params["offset"] = offset;
    if (sort && sort.length > 0) {
      params["sort"] = sort;
    }

    for (let key in params) {
      queryParamAppendStr += `${key}=${params[key]}&`;
    }
    //remove the last '&' character
    queryParamAppendStr = queryParamAppendStr.slice(0, -1);
    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-number-of-student-proficiency-level2-data/?${queryParamAppendStr}`
    );
  };

  const fetchMinistryStudentImprovementPercentageTable = (
    subject,
    district,
    school,
    sort,
    limit,
    offset,
    round
  ) => {
    let queryParamAppendStr = "";
    if (district && school && school.length > 2) {
      queryParamAppendStr += `?district=${district}&school=${school}&limit=${limit}&offset=${offset}`;
    } else if (district) {
      queryParamAppendStr += `?district=${district}&limit=${limit}&offset=${offset}`;
    } else if (school && school.length > 2) {
      queryParamAppendStr += `?school=${school}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `?limit=${limit}&offset=${offset}`;
    }
    if (sort && sort.length > 0) {
      queryParamAppendStr += `&sort=${sort}`;
    }
    if(round){
      queryParamAppendStr += `&round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-percentage-student-improved-data/${queryParamAppendStr}`
    );
  };

  const fetchMinistryStudentImprovementNumberTable = (
    subject,
    district,
    school,
    sort,
    limit,
    offset,
    round
  ) => {
    let queryParamAppendStr = "";
    if (district && school && school.length > 2) {
      queryParamAppendStr += `?district=${district}&school=${school}&limit=${limit}&offset=${offset}`;
    } else if (district) {
      queryParamAppendStr += `?district=${district}&limit=${limit}&offset=${offset}`;
    } else if (school && school.length > 2) {
      queryParamAppendStr += `?school=${school}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `?limit=${limit}&offset=${offset}`;
    }
    if (sort && sort.length > 0) {
      queryParamAppendStr += `&sort=${sort}`;
    }
    if(round){
      queryParamAppendStr += `&round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-number-of-student-improved-data/${queryParamAppendStr}`
    );
  };

  const fetchMinistryQuestionsAttemptedTable = (
    subject,
    district,
    school,
    sort,
    limit,
    offset,
    round
  ) => {
    let queryParamAppendStr = "";
    if (district && school && school.length > 2) {
      queryParamAppendStr += `?district=${district}&school=${school}&limit=${limit}&offset=${offset}`;
    } else if (district) {
      queryParamAppendStr += `?district=${district}&limit=${limit}&offset=${offset}`;
    } else if (school && school.length > 2) {
      queryParamAppendStr += `?school=${school}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `?limit=${limit}&offset=${offset}`;
    }
    if (sort && sort.length > 0) {
      queryParamAppendStr += `&sort=${sort}`;
    }
    if(round){
      queryParamAppendStr += `&round_no=${round}`;
    }

    return api.get(
      `/api/pisa-dashboard/moe${isDemoAPI}/subject/${subject}/fetch-moe-question-attempted-table-data/${queryParamAppendStr}`
    );
  };

  const fetchStudentDashboardOverviewData = (class_code, round) => {
    let queryParamAppendStr = "";
    if (round) {
      queryParamAppendStr += `?round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/student${isDemoAPI}/class_code/${class_code}/pisa-overview-screen-data/${queryParamAppendStr}`
    );
  };

  const fetchStudentTimeChartData = (class_code, filter_class) => {
    return api.get(
      `/api/pisa-dashboard/student${isDemoAPI}/class_code/${class_code}/pisa-overview-screen-time-data/${
        filter_class ? `?filter_class=${filter_class}` : ""
      }`
    );
  };

  const fetchStudentQuestionCompletionChartData = (
    class_code,
    filter_class,
    round
  ) => {
    //create a query param string for the filter_class and round
    let queryParamAppendStr = "";
    if (filter_class) {
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if (round) {
      queryParamAppendStr += `${filter_class ? "&" : "?"}round_no=${round}`;
    }
   
    return api.get(
      `/api/pisa-dashboard/student${isDemoAPI}/class_code/${class_code}/pisa-overview-screen-question-completed-data/${queryParamAppendStr}`
    );
  };

  const fetchStudentImprovementChartData = (class_code, filter_class, round) => {
    //create a query param string for the filter_class and round
    let queryParamAppendStr = "";
    if (filter_class) {
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if (round) {
      queryParamAppendStr += `${filter_class ? "&" : "?"}round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/student${isDemoAPI}/class_code/${class_code}/pisa-overview-screen-improvement-data/${queryParamAppendStr}`
    );
  };

  const fetchStudentTimeData = (class_code, format) => {
    return api.get(
      `/api/pisa-dashboard/student${isDemoAPI}/class_code/${class_code}/pisa-fetch-student-time-data/?time_format=${format}`
    );
  };

  const fetchStudentQuestionCompletionData = (class_code, round) => {
    return api.get(
      `/api/pisa-dashboard/student${isDemoAPI}/class_code/${class_code}/pisa-fetch-question-completion-data/?round_no=${round}`
    );
  };

  const fetchStudentImprovementData = (class_code, round) => {
    return api.get(
      `/api/pisa-dashboard/student${isDemoAPI}/class_code/${class_code}/pisa-fetch-improvements-data/?round_no=${round}`
    );
  };

  const fetchStudentDetailedImprovementData = (class_code, student_id, round) => {
    return api.get(
      `/api/pisa-dashboard/student${isDemoAPI}/class_code/${class_code}/student/${student_id}/pisa-fetch-detailed-improvements-data/?round_no=${round}`
    );
  };

  const fetchStudentTopicCompletedQuestionData = (
    class_code,
    student_id,
    topic_id
  ) => {
    return api.get(
      `/api/pisa-dashboard/student${isDemoAPI}/class_code/${class_code}/topic/${topic_id}/student/${student_id}/pisa-fetch-topic-completed-questions/`
    );
  };

  const fetchStudentTriesQuestionData = (
    class_code,
    student_id,
    topic_id,
    try_count
  ) => {
    return api.get(
      `/api/pisa-dashboard/student${isDemoAPI}/class_code/${class_code}/topic/${topic_id}/student/${student_id}/try_number/${try_count}/pisa-fetch-improvement-question-data/`
    );
  };

  const fetchStudentPracticeReportQuestions = (class_code, attempt_id) => {
    return api.get(
      `/api/pisa-dashboard/student${isDemoAPI}/class_code/${class_code}/attempt/${attempt_id}/practice-question/`
    );
  };

  const fetchStudentImprovementReportQuestions = (class_code, attempt_id) => {
    return api.get(
      `/api/pisa-dashboard/student${isDemoAPI}/class_code/${class_code}/attempt/${attempt_id}/improvement-question/`
    );
  };

  const fetchTeacherDashboardOverviewData = (subject) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-teacher-overview-dashboard-data/`
    );
  };

  const fetchTeacherDashboardNationwideStudentEngagementCount = (subject) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-nationwide-student-engagement-count/`
    );
  };

  const fetchTeacherDashboardNationwideStudentEngagementChart = (subject) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-nationwide-student-engagement-details/`
    );
  };

  const fetchTeacherDashboardNationwideStudentProfLvlTwoCount = (subject) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-nationwide-students-reached-proficieny-level2-count/`
    );
  };

  const fetchTeacherDashboardNationwideStudentProfLvlTwoChart = (subject) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-nationwide-student-proficiency-level2-details/`
    );
  };
  const fetchTeacherDashboardNationwideStudentProfLvlThreeCount = (subject) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-nationwide-students-reached-proficieny-level2-count/?round_no=2`
    );
  };

  const fetchTeacherDashboardNationwideStudentProfLvlThreeChart = (subject) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-nationwide-student-proficiency-level2-details/?round_no=2`
    );
  };

  const fetchTeacherDashboardStudentEngagementCount = (subject) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-teacher-school-student-engagement-count/`
    );
  };

  const fetchTeacherDashboardStudentEngagementChart = (
    subject,
    filter_class
  ) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-teacher-school-student-engagement-data/${
        filter_class ? `?filter_class=${filter_class}` : ""
      }`
    );
  };

  const fetchTeacherDashboardStudentCompletedTwentyQuestionsCount = (
    subject
  ) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-teacher-school-student-completed-atleast-20-questions-count/`
    );
  };

  const fetchTeacherDashboardStudentCompletedTwentyQuestionsChart = (
    subject,
    filter_class
  ) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-teacher-school-student-average-question-completion-data/${
        filter_class ? `?filter_class=${filter_class}` : ""
      }`
    );
  };
  const fetchTeacherDashboardStudentCompletedTenQuestionsCount = (
    subject
  ) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-teacher-school-student-completed-atleast-20-questions-count/?round_no=2`
    );
  };

  const fetchTeacherDashboardStudentCompletedTenQuestionsChart = (
    subject,
    filter_class
  ) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-teacher-school-student-average-question-completion-data/?round_no=2${
        filter_class ? `&filter_class=${filter_class}` : ""
      }`
    );
  };

  const fetchTeacherDashboardStudentProfLvlTwoCount = (subject, round) => {
    let queryParamAppendStr = "";
    if (round) {
      queryParamAppendStr += `?round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-teacher-school-students-reached-proficieny-level2-count/${queryParamAppendStr}`
    );
  };

  const fetchTeacherDashboardStudentProfLvlTwoChart = (
    subject,
    filter_class,
    round
  ) => {
    let queryParamAppendStr = "";
    if(filter_class){
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if(round){
      queryParamAppendStr += `${filter_class ? "&" : "?"}round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-teacher-school-students-reached-proficieny-level2-details/${queryParamAppendStr}`
    );
  };

  const fetchTeacherDashboardStudentImprovementCount = (subject, round) => {
    let queryParamAppendStr = "";
    if (round) {
      queryParamAppendStr += `?round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-teacher-school-student-improvement-count/${queryParamAppendStr}`
    );
  };

  const fetchTeacherDashboardStudentImprovementChart = (
    subject,
    filter_class,
    round
  ) => {
    let queryParamAppendStr = "";
    if(filter_class){
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if(round){
      queryParamAppendStr += `${filter_class ? "&" : "?"}round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-teacher-school-student-improvement-details/${queryParamAppendStr}`
    );
  };

  const fetchTeacherDashboardStudentHoursTable = (
    subject,
    time_filter,
    filter_class,
    name,
    limit,
    offset
  ) => {
    let queryParamAppendStr = `?time_format=${time_filter}`;
    if (filter_class && filter_class.length > 0 && name && name.length > 2) {
      queryParamAppendStr += `&filter_class=${filter_class}&name=${name}&limit=${limit}&offset=${offset}`;
    } else if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `&filter_class=${filter_class}&limit=${limit}&offset=${offset}`;
    } else if (name && name.length > 2) {
      queryParamAppendStr += `&name=${name}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `&limit=${limit}&offset=${offset}`;
    }

    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/pisa-fetch-student-usage/${queryParamAppendStr}`
    );
  };

  const fetchTeacherDashboardStudentQuestionsCompletedTable = (
    subject,
    filter_class,
    name,
    limit,
    offset,
    round
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0 && name && name.length > 2) {
      queryParamAppendStr += `?filter_class=${filter_class}&name=${name}&limit=${limit}&offset=${offset}`;
    } else if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}&limit=${limit}&offset=${offset}`;
    } else if (name && name.length > 2) {
      queryParamAppendStr += `?name=${name}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `?limit=${limit}&offset=${offset}`;
    }

    if(round){  
      queryParamAppendStr += `&round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/pisa-fetch-question-completion-data/${queryParamAppendStr}`
    );
  };

  const fetchTeacherDashboardStudentProfLvlTwoTable = (
    subject,
    filter_class,
    name,
    limit,
    offset,
    round
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0 && name && name.length > 2) {
      queryParamAppendStr += `?filter_class=${filter_class}&name=${name}&limit=${limit}&offset=${offset}`;
    } else if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}&limit=${limit}&offset=${offset}`;
    } else if (name && name.length > 2) {
      queryParamAppendStr += `?name=${name}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `?limit=${limit}&offset=${offset}`;
    }
    if(round){
      queryParamAppendStr += `&round_no=${round}`;
    }

    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/pisa-fetch-student-proficient-table-data/${queryParamAppendStr}`
    );
  };

  const fetchTeacherDashboardStudentImprovementTable = (
    subject,
    filter_class,
    name,
    limit,
    offset,
    round
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0 && name && name.length > 2) {
      queryParamAppendStr += `?filter_class=${filter_class}&name=${name}&limit=${limit}&offset=${offset}`;
    } else if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}&limit=${limit}&offset=${offset}`;
    } else if (name && name.length > 2) {
      queryParamAppendStr += `?name=${name}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `?limit=${limit}&offset=${offset}`;
    }
    if(round){
      queryParamAppendStr += `&round_no=${round}`;
    }

    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/pisa-fetch-improvements-data/${queryParamAppendStr}`
    );
  };

  const fetchTeacherDashboardStudentDetailedImprovementTable = (
    subject,
    student_id,
    filter_class,
    round
  ) => {
    let queryParamAppendStr = "";
    if(filter_class){
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if(round){
      queryParamAppendStr += `${filter_class ? "&" : "?"}round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/student/${student_id}/pisa-teacher-fetch-detailed-improvements-data/${queryParamAppendStr}`
    );
  };

  const fetchTeacherDashboardQuestionAnalysisChart = (
    subject,
    filter_class,
    round
  ) => {
    let queryParamAppendStr = "";
    if(filter_class){
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if(round){
      queryParamAppendStr += `${filter_class ? "&" : "?"}round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/incorrect-attempts-of-question-processes/${queryParamAppendStr}`
    );
  };

  const fetchTeacherDashboardStudentTopicCompletedQuestionData = (
    subject,
    student_id,
    topic_id
  ) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/topic/${topic_id}/student/${student_id}/pisa-teacher-fetch-topic-completed-questions/`
    );
  };

  const fetchTeacherDashboardStudentTriesQuestionData = (
    subject,
    student_id,
    topic_id,
    try_count
  ) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/topic/${topic_id}/student/${student_id}/try_number/${try_count}/pisa-teacher-fetch-improvement-question-data/`
    );
  };

  const fetchTeacherDashboardStudentPracticeReportQuestions = (
    subject,
    attempt_id
  ) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/attempt/${attempt_id}/practice-question/`
    );
  };

  const fetchTeacherDashboardStudentImprovementReportQuestions = (
    subject,
    attempt_id
  ) => {
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/attempt/${attempt_id}/improvement-question/`
    );
  };

  const fetchTeacherDashboardQuestionAnalysisTopics = (
    subject,
    filter_class,
    round
  ) => {
    let queryParamAppendStr = "";
    if(filter_class){
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if(round){
      queryParamAppendStr += `${filter_class ? "&" : "?"}round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/fetch-topics/${queryParamAppendStr}`
    );
  };

  const fetchTeacherDashboardQuestionAnalysisTable = (
    subject,
    filter_class,
    topic_id,
    round
  ) => {
    let queryParamAppendStr = "";
    if(filter_class){
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if(round){
      queryParamAppendStr += `${filter_class ? "&" : "?"}round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/topic/${topic_id}/question-analysis-data/${queryParamAppendStr}`
    );
  };

  const fetchStudentListDropdown = (subject, filter_class, round) => {
    let queryParamAppendStr = "";
    if (filter_class) {
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if (round) {
      queryParamAppendStr += `${filter_class ? "&" : "?"}round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/teacher${isDemoAPI}/subject/${subject}/students/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardOverviewData = (subject) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-principal-overview-dashboard-data/`
    );
  };

  const fetchPrincipalDashboardNationwideTeacherEngagementCount = (subject) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-nationwide-teacher-engagement-count/`
    );
  };

  const fetchPrincipalDashboardNationwideTeacherEngagementChart = (subject) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-nationwide-teacher-engagement-details/`
    );
  };

  const fetchPrincipalDashboardNationwideStudentEngagementCount = (subject) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-nationwide-student-engagement-count/`
    );
  };

  const fetchPrincipalDashboardNationwideStudentEngagementChart = (subject) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-nationwide-student-engagement-details/`
    );
  };

  const fetchPrincipalDashboardNationwideStudentProfLvlTwoCount = (subject) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-nationwide-students-reached-proficieny-level2-count/`
    );
  };

  const fetchPrincipalDashboardNationwideStudentProfLvlTwoChart = (subject) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-nationwide-student-proficiency-level2-details/`
    );
  };

  const fetchPrincipalDashboardNationwideStudentProfLvlThreeCount = (subject) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-nationwide-students-reached-proficieny-level2-count/?round_no=2`
    );
  };

  const fetchPrincipalDashboardNationwideStudentProfLvlThreeChart = (subject) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-nationwide-student-proficiency-level2-details/?round_no=2`
    );
  };

  const fetchPrincipalDashboardTeacherEngagementCount = (subject) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-principal-school-teacher-engagement-count/`
    );
  };

  const fetchPrincipalDashboardTeacherEngagementChart = (
    subject,
    filter_class
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-principal-school-teacher-engagement-data/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardStudentEngagementCount = (subject) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-principal-school-student-engagement-count/`
    );
  };

  const fetchPrincipalDashboardStudentEngagementChart = (
    subject,
    filter_class
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }

    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-principal-school-student-engagement-data/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardStudentCompletedTwentyQuestionsCount = (
    subject
  ) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-principal-school-student-completed-atleast-20-questions-count/`
    );
  };

  const fetchPrincipalDashboardStudentCompletedTwentyQuestionsChart = (
    subject,
    filter_class
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-principal-school-student-average-question-completion-data/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardStudentCompletedTenQuestionsCount = (
    subject
  ) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-principal-school-student-completed-atleast-20-questions-count/?round_no=2`
    );
  };

  const fetchPrincipalDashboardStudentCompletedTenQuestionsChart = (
    subject,
    filter_class
  ) => {
    let queryParamAppendStr = "?round_no=2";
    if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `&filter_class=${filter_class}`;
    }

    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-principal-school-student-average-question-completion-data/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardStudentProfLvlTwoCount = (subject, round) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-principal-school-students-reached-proficieny-level2-count/${round ? `?round_no=${round}` : ""}`
    );
  };

  const fetchPrincipalDashboardStudentProfLvlTwoChart = (
    subject,
    filter_class,
    round
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if (round) {
      queryParamAppendStr += `${(filter_class && filter_class.length > 0) ? "&" : "?"}round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-principal-school-students-reached-proficieny-level2-details/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardStudentImprovementCount = (subject, round) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-principal-school-student-improvement-count/${round ? `?round_no=${round}` : ""}`
    );
  };

  const fetchPrincipalDashboardStudentImprovementChart = (
    subject,
    filter_class,
    round
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if (round) {
      queryParamAppendStr += `${(filter_class && filter_class.length > 0) ? "&" : "?"}round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-principal-school-student-improvement-details/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardTeacherHoursTable = (
    subject,
    time_filter,
    filter_class,
    name,
    limit,
    offset
  ) => {
    let queryParamAppendStr = `?time_format=${time_filter}`;
    if (filter_class && filter_class.length > 0 && name && name.length > 2) {
      queryParamAppendStr += `&filter_class=${filter_class}&name=${name}&limit=${limit}&offset=${offset}`;
    } else if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `&filter_class=${filter_class}&limit=${limit}&offset=${offset}`;
    } else if (name && name.length > 2) {
      queryParamAppendStr += `&name=${name}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `&limit=${limit}&offset=${offset}`;
    }

    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/pisa-fetch-teacher-usage/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardStudentHoursTable = (
    subject,
    time_filter,
    filter_class,
    name,
    limit,
    offset
  ) => {
    let queryParamAppendStr = `?time_format=${time_filter}`;
    if (filter_class && filter_class.length > 0 && name && name.length > 2) {
      queryParamAppendStr += `&filter_class=${filter_class}&name=${name}&limit=${limit}&offset=${offset}`;
    } else if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `&filter_class=${filter_class}&limit=${limit}&offset=${offset}`;
    } else if (name && name.length > 2) {
      queryParamAppendStr += `&name=${name}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `&limit=${limit}&offset=${offset}`;
    }

    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/pisa-fetch-student-usage/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardStudentQuestionsCompletedTable = (
    subject,
    filter_class,
    name,
    limit,
    offset,
    round
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0 && name && name.length > 2) {
      queryParamAppendStr += `?filter_class=${filter_class}&name=${name}&limit=${limit}&offset=${offset}`;
    } else if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}&limit=${limit}&offset=${offset}`;
    } else if (name && name.length > 2) {
      queryParamAppendStr += `?name=${name}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `?limit=${limit}&offset=${offset}`;
    }
    if(round){
      queryParamAppendStr += `&round_no=${round}`;
    }

    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/pisa-fetch-question-completion-data/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardStudentProfLvlTwoTable = (
    subject,
    filter_class,
    name,
    limit,
    offset,
    round
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0 && name && name.length > 2) {
      queryParamAppendStr += `?filter_class=${filter_class}&name=${name}&limit=${limit}&offset=${offset}`;
    } else if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}&limit=${limit}&offset=${offset}`;
    } else if (name && name.length > 2) {
      queryParamAppendStr += `?name=${name}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `?limit=${limit}&offset=${offset}`;
    }
    if(round){
      queryParamAppendStr += `&round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/pisa-fetch-student-proficient-table-data/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardStudentImprovementTable = (
    subject,
    filter_class,
    name,
    limit,
    offset,
    round
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0 && name && name.length > 2) {
      queryParamAppendStr += `?filter_class=${filter_class}&name=${name}&limit=${limit}&offset=${offset}`;
    } else if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}&limit=${limit}&offset=${offset}`;
    } else if (name && name.length > 2) {
      queryParamAppendStr += `?name=${name}&limit=${limit}&offset=${offset}`;
    } else {
      queryParamAppendStr += `?limit=${limit}&offset=${offset}`;
    }
    if(round){
      queryParamAppendStr += `&round_no=${round}`;
    }

    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/pisa-fetch-improvements-data/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardStudentDetailedImprovementTable = (
    subject,
    student_id,
    filter_class,
    round
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if (round) {
      queryParamAppendStr += `${(filter_class && filter_class.length > 0) ? "&" : "?"}round_no=${round}`;
    }

    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/student/${student_id}/pisa-principal-fetch-detailed-improvements-data/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardStudentTopicCompletedQuestionData = (
    subject,
    student_id,
    topic_id
  ) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/topic/${topic_id}/student/${student_id}/pisa-principal-fetch-topic-completed-questions/`
    );
  };

  const fetchPrincipalDashboardStudentTriesQuestionData = (
    subject,
    student_id,
    topic_id,
    try_count
  ) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/topic/${topic_id}/student/${student_id}/try_number/${try_count}/pisa-principal-fetch-improvement-question-data/`
    );
  };

  const fetchPrincipalDashboardStudentPracticeReportQuestions = (
    subject,
    attempt_id
  ) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/attempt/${attempt_id}/practice-question/`
    );
  };

  const fetchPrincipalDashboardStudentImprovementReportQuestions = (
    subject,
    attempt_id
  ) => {
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/attempt/${attempt_id}/improvement-question/`
    );
  };

  const fetchPrincipalDashboardQuestionAnalysisChart = (
    subject,
    filter_class,
    round
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if (round) {
      queryParamAppendStr += `${(filter_class && filter_class.length > 0) ? "&" : "?"}round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/incorrect-attempts-of-question-processes/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardQuestionAnalysisTopics = (
    subject,
    filter_class,
    round
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if (round) {
      queryParamAppendStr += `${(filter_class && filter_class.length > 0) ? "&" : "?"}round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/fetch-topics/${queryParamAppendStr}`  
    );
  };

  const fetchPrincipalDashboardQuestionAnalysisTable = (
    subject,
    filter_class,
    topic_id,
    round
  ) => {
    let queryParamAppendStr = "";
    if (filter_class) {
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    if (round) {
      queryParamAppendStr += `${(filter_class ? "&" : "?")}round_no=${round}`;
    }
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/topic/${topic_id}/question-analysis-data/${queryParamAppendStr}`
    );
  };

  const fetchPrincipalDashboardStudentListDropdown = (
    subject,
    filter_class
  ) => {
    let queryParamAppendStr = "";
    if (filter_class && filter_class.length > 0) {
      queryParamAppendStr += `?filter_class=${filter_class}`;
    }
    return api.get(
      `/api/pisa-dashboard/principal${isDemoAPI}/subject/${subject}/students/${queryParamAppendStr}`
    );
  };

  const fetchDistrictSchools = (limit, offset, name, district) => {
    let queryParamAppendStr = `?limit=${limit}&offset=${offset}`;
    if (name && name.length > 0) {
      queryParamAppendStr += `&name=${name}`;
    }
    if (district && district.length > 0) {
      queryParamAppendStr += `&district=${district}`;
    }

    return api.get(
      `/api/pisa-dashboard/district-schools/${queryParamAppendStr}`
    );
  };

  const fetchDistricts = (limit, offset, name) => {
    return api.get(
      `/api/pisa-dashboard/country-districts/?limit=${limit}&offset=${offset}${
        name && name.length > 2 ? `&name=${name}` : ""
      }`
    );
  };

  const downloadDistrictData = (district,country) => {
    return api.post(
      `/api/pisa-dashboard/admin-report/?country=${country}${
        district && district.length > 0 ? `&district=${district}` : ""
      }`
    );
  }
  const downloadAllMOEReports = (
    subject,
    language,
    AllMoeReportsDownloadPayloadData
  ) => {
    const apiEndpoints = {
      0: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-teacher-login-atleast-once-data/`,
      1: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-detailed-teacher-engagement-data/`,
      2: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-student-login-atleast-once-data/`,
      3: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-detailed-student-engagement-data/`,
      4: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-percentage-student-proficiency-level2-data/`,
      5: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-number-of-student-proficiency-level2-data/`,
      6: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-question-attempted-table-data/`,
      7: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-percentage-student-improved-data/`,
      8: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-number-of-student-improved-data/`,
      9: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-percentage-student-proficiency-level2-data/`,
      10: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-number-of-student-proficiency-level2-data/`,
      11: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-question-attempted-table-data/`,
      12: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-percentage-student-improved-data/`,
      13: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-number-of-student-improved-data/`,
    };
    const data = AllMoeReportsDownloadPayloadData.map((data,index) => {
      return index < 9 ? {
        api_endpoint: apiEndpoints[index],
        metric_name: data
      } : {
        api_endpoint: apiEndpoints[index],
        metric_name: data,
        round_no: 2
      }
    });

    const payload = {
      language: language,
      data: data,
    }
    return api.post(
      `/api/pisa-dashboard/download-all-moe-reports/`, payload
    );
  };

const downloadPaginationData = (param, subject,language,metricName, topic,round) => {
  const apiEndpoints = {
    0: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-teacher-login-atleast-once-data/`,
    1: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-detailed-teacher-engagement-data/`,
    2: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-student-login-atleast-once-data/`,
    3: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-detailed-student-engagement-data/`,
    4: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-percentage-student-proficiency-level2-data/`,
    5: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-number-of-student-proficiency-level2-data/`,
    6: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-percentage-student-improved-data/`,
    7: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-number-of-student-improved-data/`,
    8: `/api/pisa-dashboard/moe/subject/${subject}/fetch-moe-question-attempted-table-data/`,

    9: `/api/pisa-dashboard/principal/subject/${subject}/pisa-fetch-teacher-usage/`,
    10: `/api/pisa-dashboard/principal/subject/${subject}/pisa-fetch-student-usage/`,
    11: `/api/pisa-dashboard/principal/subject/${subject}/pisa-fetch-question-completion-data/`,
    12: `/api/pisa-dashboard/principal/subject/${subject}/pisa-fetch-student-proficient-table-data/`,
    13: `/api/pisa-dashboard/principal/subject/${subject}/pisa-fetch-improvements-data/`,

    14: `/api/pisa-dashboard/teacher/subject/${subject}/pisa-fetch-student-usage/`,
    15: `/api/pisa-dashboard/teacher/subject/${subject}/pisa-fetch-question-completion-data/`,
    16: `/api/pisa-dashboard/teacher/subject/${subject}/pisa-fetch-student-proficient-table-data/`,
    17: `/api/pisa-dashboard/teacher/subject/${subject}/pisa-fetch-improvements-data/`,

    18: `/api/pisa-dashboard/principal/subject/${subject}/topic/${topic?.topic_id}/question-analysis-data/`,
    19: `/api/pisa-dashboard/teacher/subject/${subject}/topic/${topic?.topic_id}/question-analysis-data/`,
  };

  const apiEndpoint = apiEndpoints[param];

  const body = topic ?{
    api_endpoint: apiEndpoint,
    language: language,
    metric_name: metricName,
    topic_key: topic.name,
    round_no: round ? round : 1,
  } : {
    api_endpoint: apiEndpoint,
    language: language,
    metric_name: metricName,
    round_no : round ? round : 1,
  };

  return api.post(`/api/pisa-dashboard/download-reports/`, body);
};

  return {
    setAccessToken,
    setPrincipalSchoolId,
    removeAccessToken,
    check401,
    loginUser,
    getMe,
    refreshUser,
    forgotPassword,
    verifyResetCode,
    resetPassword,
    changePassword,
    logoutUser,
    fetchMinistryDashboardOverviewData,
    fetchTeacherEngagementCount,
    fetchTeacherEngagementData,
    fetchStudentEngagementCount,
    fetchStudentEngagementData,
    fetchStudentProficientLvlTwoCount,
    fetchStudentProficientLvlTwoData,
    fetchMinistryTotalQuestionsAttemptCount,
    fetchMinistryTotalQuestionsAttemptData,
    fetchMinistryStudentImprovementCount,
    fetchMinistryStudentImprovementData,
    fetchTeacherEngagementPercentageTable,
    fetchTeacherEngagementHoursTable,
    fetchStudentEngagementPercentageTable,
    fetchStudentEngagementHoursTable,
    fetchStudentProficientLvlTwoPercentTable,
    fetchStudentProficientLvlTwoNumberTable,
    fetchMinistryStudentImprovementPercentageTable,
    fetchMinistryStudentImprovementNumberTable,
    fetchMinistryQuestionsAttemptedTable,
    fetchStudentDashboardOverviewData,
    fetchStudentTimeChartData,
    fetchStudentQuestionCompletionChartData,
    fetchStudentImprovementChartData,
    fetchStudentTimeData,
    fetchStudentQuestionCompletionData,
    fetchStudentImprovementData,
    fetchStudentDetailedImprovementData,
    fetchStudentTopicCompletedQuestionData,
    fetchStudentTriesQuestionData,
    fetchStudentPracticeReportQuestions,
    fetchStudentImprovementReportQuestions,
    fetchTeacherDashboardOverviewData,
    fetchTeacherDashboardNationwideStudentEngagementCount,
    fetchTeacherDashboardNationwideStudentEngagementChart,
    fetchTeacherDashboardNationwideStudentProfLvlTwoCount,
    fetchTeacherDashboardNationwideStudentProfLvlTwoChart,
    fetchTeacherDashboardNationwideStudentProfLvlThreeCount,
    fetchTeacherDashboardNationwideStudentProfLvlThreeChart,
    fetchTeacherDashboardStudentEngagementCount,
    fetchTeacherDashboardStudentEngagementChart,
    fetchTeacherDashboardStudentCompletedTwentyQuestionsCount,
    fetchTeacherDashboardStudentCompletedTwentyQuestionsChart,
    fetchTeacherDashboardStudentCompletedTenQuestionsCount,
    fetchTeacherDashboardStudentCompletedTenQuestionsChart,

    fetchTeacherDashboardStudentProfLvlTwoCount,
    fetchTeacherDashboardStudentProfLvlTwoChart,
    fetchTeacherDashboardStudentImprovementCount,
    fetchTeacherDashboardStudentImprovementChart,

    fetchTeacherDashboardStudentHoursTable,
    fetchTeacherDashboardStudentQuestionsCompletedTable,
    fetchTeacherDashboardStudentProfLvlTwoTable,
    fetchTeacherDashboardStudentImprovementTable,
    fetchTeacherDashboardStudentDetailedImprovementTable,
    fetchTeacherDashboardStudentTopicCompletedQuestionData,
    fetchTeacherDashboardStudentTriesQuestionData,
    fetchTeacherDashboardStudentPracticeReportQuestions,
    fetchTeacherDashboardStudentImprovementReportQuestions,
    fetchTeacherDashboardQuestionAnalysisTopics,
    fetchTeacherDashboardQuestionAnalysisChart,
    fetchTeacherDashboardQuestionAnalysisTable,
    fetchStudentListDropdown,
    fetchPrincipalDashboardOverviewData,
    fetchPrincipalDashboardNationwideTeacherEngagementCount,
    fetchPrincipalDashboardNationwideTeacherEngagementChart,
    fetchPrincipalDashboardNationwideStudentEngagementCount,
    fetchPrincipalDashboardNationwideStudentEngagementChart,
    fetchPrincipalDashboardNationwideStudentProfLvlTwoCount,
    fetchPrincipalDashboardNationwideStudentProfLvlTwoChart,
    fetchPrincipalDashboardNationwideStudentProfLvlThreeCount,
    fetchPrincipalDashboardNationwideStudentProfLvlThreeChart,
    fetchPrincipalDashboardTeacherEngagementCount,
    fetchPrincipalDashboardTeacherEngagementChart,
    fetchPrincipalDashboardStudentEngagementCount,
    fetchPrincipalDashboardStudentEngagementChart,
    fetchPrincipalDashboardStudentCompletedTwentyQuestionsCount,
    fetchPrincipalDashboardStudentCompletedTwentyQuestionsChart,
    fetchPrincipalDashboardStudentCompletedTenQuestionsCount,
    fetchPrincipalDashboardStudentCompletedTenQuestionsChart,

    fetchPrincipalDashboardStudentProfLvlTwoCount,
    fetchPrincipalDashboardStudentProfLvlTwoChart,
    fetchPrincipalDashboardStudentImprovementCount,
    fetchPrincipalDashboardStudentImprovementChart,
    fetchPrincipalDashboardTeacherHoursTable,
    fetchPrincipalDashboardStudentHoursTable,
    fetchPrincipalDashboardStudentQuestionsCompletedTable,
    fetchPrincipalDashboardStudentProfLvlTwoTable,
    fetchPrincipalDashboardStudentImprovementTable,
    fetchPrincipalDashboardStudentDetailedImprovementTable,
    fetchPrincipalDashboardStudentTopicCompletedQuestionData,
    fetchPrincipalDashboardStudentTriesQuestionData,
    fetchPrincipalDashboardStudentPracticeReportQuestions,
    fetchPrincipalDashboardStudentImprovementReportQuestions,
    fetchPrincipalDashboardQuestionAnalysisChart,
    fetchPrincipalDashboardQuestionAnalysisTopics,
    fetchPrincipalDashboardQuestionAnalysisTable,
    fetchPrincipalDashboardStudentListDropdown,
    fetchDistrictSchools,
    fetchDistricts,
    downloadDistrictData,
    downloadAllMOEReports,
    downloadPaginationData,
  };
};

export default create();
